import React, { useEffect, useRef, useState } from "react";
import "./mainLayout.scss";
import Countdown from "react-countdown";
import { dateDiffMilliSeconds } from "../../utils/utils.js";
import { useGlobalState } from "../../GlobalStates.js";
import CloseIcon from "@mui/icons-material/Close";
import useIsAndroid from "../../customHooks/useIsAndroid.js";
import { DownloadAppModal } from "../DownloadAppModal.js";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../hooks/responsive.js";
import dayjs from "dayjs";
import { resolveCtaNavigation } from "../../utils/reroute.js";
import { reportBottomBanner, useTabBarOverlay } from "./appTabBar";

const DownloadAppFooter = ({ children }) => {
  const { t } = useTranslation();
  const [searchMobile, setSearchMobile] = useGlobalState("searchMobile");
  const [conciergeOpen] = useGlobalState("conciergeOpen");
  const [activeLuckyDrawCampaign] = useGlobalState("activeLuckyDrawCampaign");
  const [, setOpenLuckyDrawGame] = useGlobalState("openLuckyDrawGame");

  const isMobile = useIsMobile();

  const isInApp = window.isInApp;
  const [showAppHeader, setShowAppHeader] = useState(true);
  const [openDownloadAppModal, setOpenDownloadAppModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isAndroid = useIsAndroid();

  const handleDownloadClick = () => {
    if (isMobile) {
      // const appStoreRedirect = isAndroid
      //   ? "https://play.google.com/store/apps/details?id=com.sotravel.sotravel"
      //   : "https://apps.apple.com/app/id6447177449";
      // window.open("https://www.sotravel.com/app-redirect/Explore");
      navigate("/app-redirect/Explore/nowebredirect");
      setShowAppHeader(false);
    } else {
      setOpenDownloadAppModal(true);
    }
  };

  const handleSeeDealsClick = (event) => {
    event?.stopPropagation();
    if (activeLuckyDrawCampaign) {
      setOpenLuckyDrawGame(true);
      setShowAppHeader(false);
      return;
    }
    const navigation = resolveCtaNavigation(
      promotion?.generalPromo?.cta,
      window.location.origin
    );
    if (!navigation) return;

    // In the app always open it in place: the webview drops new windows (a
    // dead tap on wide screens such as iPads).
    if (isMobile || isInApp) {
      if (navigation.requiresDocumentNavigation) {
        window.location.assign(navigation.href);
      } else {
        navigate(navigation.path);
      }
    } else {
      window.open(navigation.href, "_blank", "noopener,noreferrer");
    }
  };

  const shouldShowAppHeader = () => {
    if (
      location.pathname.toLowerCase().includes("/all-trips") &&
      showAppHeader &&
      !isInApp &&
      localStorage.getItem("JWT_TOKEN") &&
      !searchMobile &&
      !conciergeOpen
    ) {
      return true;
    }
  };

  const [promotion, setPromotion] = useGlobalState("promotion");

  const shouldShowPromoFooter = () => {
    if (
      (location.pathname === "/" || location.pathname.toLowerCase().includes("/all-trips")) &&
      showAppHeader &&
      promotion?.generalPromo &&
      !searchMobile &&
      !conciergeOpen &&
      Object.keys(promotion?.generalPromo).length > 0
    ) {
      return true;
    }

    return false;
  };

  const promoFooterVisible = shouldShowPromoFooter();

  // In the app, the floating tab bar sits just above this banner: report its
  // height while it's shown (0 when it goes away). See appTabBar.js.
  const tabBarOverlay = useTabBarOverlay();
  const promoFooterRef = useRef(null);
  useEffect(() => {
    const el = promoFooterRef.current;
    if (!promoFooterVisible || !el) return undefined;
    const report = () => reportBottomBanner(el.getBoundingClientRect().height);
    report();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(report) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      reportBottomBanner(0);
    };
  }, [promoFooterVisible, tabBarOverlay]);

  if (promoFooterVisible) {
    return (
      <footer
        ref={promoFooterRef}
        className={`tw-fixed tw-bottom-0 tw-z-[99999] tw-w-full tw-cursor-pointer tw-bg-black tw-text-yellow-400 tw-shadow-lg tw-shadow-black/20 tw-transition-all tw-duration-200 tw-ease-in-out ${isMobile ? "tw-h-20" : "tw-h-24"} `}
        // Under the app's floating tab bar the banner runs to the screen edge,
        // so keep its content clear of the home indicator.
        style={tabBarOverlay ? { height: `calc(${isMobile ? "5rem" : "6rem"} + var(--app-safe-bottom, 0px))`, paddingBottom: "var(--app-safe-bottom, 0px)" } : undefined}
        onClick={handleSeeDealsClick}
        // The app's tab bar floats above this banner; its overlay check skips it.
        data-app-tabbar="ignore"
      >
        <button
          className="tw-absolute tw-right-2 tw-top-2 tw-rounded-full tw-p-1 tw-text-white hover:tw-bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            setShowAppHeader(false);
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="tw-h-5 tw-w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        <div className="tw-mx-auto tw-flex tw-h-full tw-max-w-7xl tw-items-center tw-justify-between tw-px-4">
          <div className="tw-flex tw-flex-1 tw-flex-col">
            <div className="tw-space-y-1">
              <h3 className="tw-truncate tw-text-sm tw-font-extrabold md:tw-text-base">
                {promotion.generalPromo.name}
              </h3>
              <button
                onClick={handleSeeDealsClick}
                className="tw-rounded-lg tw-bg-[deepskyblue] tw-px-4 tw-py-2 tw-text-sm tw-font-bold tw-text-white tw-duration-200"
              >
                {t("browse_deals", {
                  defaultValue: "GET DEALS",
                })}
              </button>
            </div>
          </div>

          {location.pathname.toLowerCase().includes("/all-trips") ? (
            <div className="tw-ml-4">
              <Countdown
                date={
                  dayjs().isBefore(dayjs(promotion?.generalPromo.launch_time))
                    ? promotion?.generalPromo.launch_time
                    : promotion?.generalPromo.promo_end_date_inclusive
                }
                renderer={({ days, hours, minutes, seconds }) => (
                  <div className="tw-flex tw-flex-col tw-items-center tw-space-y-1">
                    {days > 0 && (
                      <p className="tw-text-xs tw-font-bold tw-text-white">
                        {dayjs().isBefore(dayjs(promotion?.generalPromo.launch_time))
                          ? t("daystart", { count: days })
                          : t("dayleft", { count: days })}
                      </p>
                    )}

                    <div className="tw-flex tw-items-center tw-space-x-1">
                      <div className="tw-rounded-lg tw-bg-white tw-px-2 tw-py-1">
                        <span className="tw-text-sm tw-font-bold tw-text-black">
                          {String(hours).padStart(2, "0")} {t("hours")}
                        </span>
                      </div>
                      <span className="tw-text-white">:</span>
                      <div className="tw-rounded-lg tw-bg-white tw-px-2 tw-py-1">
                        <span className="tw-text-sm tw-font-bold tw-text-black">
                          {String(minutes).padStart(2, "0")} {t("minutes")}
                        </span>
                      </div>
                      {!isMobile && (
                        <>
                          <span className="tw-text-white">:</span>
                          <div className="tw-rounded-lg tw-bg-white tw-px-2 tw-py-1">
                            <span className="tw-text-sm tw-font-bold tw-text-black">
                              {String(seconds).padStart(2, "0")} {t("seconds")}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
          ) : (
            <p className="tw-line-clamp-2 tw-text-xs tw-font-medium tw-text-white/80 md:tw-text-sm">
              {promotion.generalPromo.description}
            </p>
          )}
        </div>
      </footer>
    );
  } else if (shouldShowAppHeader())
    return (
      <footer
        className="tw-fixed tw-bottom-0 tw-z-[99999] tw-flex tw-w-full tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-sky-400 tw-via-sky-500 tw-to-sky-600 tw-shadow-xl tw-backdrop-blur-xl"
        style={{ height: isMobile ? "4.5rem" : "5.5rem" }}
      >
        <button
          className="tw-absolute tw-right-3 tw-top-3 tw-rounded-full tw-p-1.5 tw-text-white/90 tw-backdrop-blur-sm tw-transition-all tw-duration-300 hover:tw-bg-white/20"
          onClick={() => setShowAppHeader(false)}
        >
          <CloseIcon className="tw-h-3.5 tw-w-3.5" />
        </button>

        <div className="tw-flex tw-w-full tw-max-w-6xl tw-flex-row tw-items-center tw-justify-between tw-px-4 md:tw-px-8">
          <div className="tw-flex tw-items-center tw-space-x-3 md:tw-space-x-5">
            <div className="tw-group tw-relative tw-flex-none">
              <img
                src="/new_logo/app-icon.png"
                className="tw-h-10 tw-w-10 tw-rounded-lg tw-object-cover tw-shadow-lg tw-transition-transform tw-duration-300 group-hover:tw-scale-105 md:tw-h-14 md:tw-w-14"
                alt="Sotravel App Icon"
              />
              <div className="tw-absolute tw-inset-0 tw-rounded-lg tw-ring-2 tw-ring-white/30 tw-transition-opacity tw-duration-300 group-hover:tw-ring-white/40"></div>
            </div>

            <div className="tw-flex tw-flex-col tw-justify-center tw-space-y-0.5 md:tw-space-y-1.5">
              <span className="tw-text-sm tw-font-extrabold tw-tracking-tight tw-text-white tw-drop-shadow-sm md:tw-text-base">
                {t("sotravel_app")}
              </span>
              <span className="tw-line-clamp-2 tw-text-xs tw-font-medium tw-tracking-wide tw-text-white/90 md:tw-line-clamp-1 md:tw-text-sm">
                {t("get_access_free_reservations")}
              </span>
            </div>
          </div>

          <button
            className="tw-text-deepskyblue tw-ml-2 tw-whitespace-nowrap tw-rounded-xl tw-border tw-border-white/20 tw-bg-white tw-px-4 tw-py-2 tw-text-xs tw-font-bold tw-shadow-lg tw-backdrop-blur-sm tw-transition-all tw-duration-300 hover:tw-scale-105 hover:tw-bg-opacity-95 hover:tw-shadow-xl active:tw-scale-100 md:tw-ml-0 md:tw-px-7 md:tw-py-3 md:tw-text-sm"
            onClick={handleDownloadClick}
          >
            {t("open_app")}
          </button>
        </div>

        {openDownloadAppModal && (
          <DownloadAppModal
            open={openDownloadAppModal}
            handleClose={() => {
              setOpenDownloadAppModal(false);
              setShowAppHeader(false);
            }}
          />
        )}
      </footer>
    );
};

export default DownloadAppFooter;
